import React, { useState } from 'react'
import { Loader, Sparkles, Film, Image as ImageIcon, ArrowRight, ExternalLink, AlertTriangle } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { SHORT_LOGO_URL } from '../../lib/brandAssets'
import {
  TOS_ARTICLES,
  TOS_VERSION,
  TOS_LAST_UPDATED,
  AI_NOTICE,
} from '../../constants/termsOfService'

interface Props {
  onAccepted: () => Promise<void> | void
}

/**
 * 全画面 Welcome 画面（利用規約同意ゲート）。
 * 左: Genemo の紹介 / 右: 利用規約とチェック / 下: 始めるボタン
 * 明るく見やすい配色（白基調 + Sky アクセント）
 */
export const TosAcceptanceOverlay: React.FC<Props> = ({ onAccepted }) => {
  const { logout } = useAuth()
  const [agreed, setAgreed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!agreed || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch('/api/tos/accept', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: TOS_VERSION }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        setError(json.error || `保存に失敗しました（${res.status}）`)
        setSubmitting(false)
        return
      }
      try {
        await onAccepted()
      } catch (err) {
        console.error('checkSession failed, reloading page:', err)
        window.location.reload()
        return
      }
      setSubmitting(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitting(false)
    }
  }

  const handleLogout = async () => {
    try { await logout() } catch {}
  }

  return (
    <div
      className="fixed inset-0 z-200 overflow-y-auto"
      style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #faf5ff 100%)',
      }}
    >
      {/* 装飾オーロラ */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[-200px] right-[-100px] w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.18) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-150px] left-[-150px] w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* ヘッダー */}
        <div className="mb-8 sm:mb-10 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row sm:items-end sm:gap-4 mb-3">
            <img
              src={SHORT_LOGO_URL}
              alt="Genemo"
              className="h-12 sm:h-14 object-contain mx-auto sm:mx-0"
            />
            <h1 className="text-3xl sm:text-4xl font-black text-ink leading-tight">
              へようこそ！
            </h1>
          </div>
          <p className="text-ink-muted text-sm sm:text-base">
            ご利用を開始する前に、利用規約をご確認のうえ、ご同意ください。
          </p>
        </div>

        {/* 2カラム */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

          {/* 左: Genemoとは */}
          <div className="bg-white rounded-2xl p-6 sm:p-7 shadow-xl shadow-sky-100/60 border border-primary">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-linear-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-md shadow-cyan-200">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-ink">Genemoとは</h2>
            </div>
            <p className="text-sm text-ink leading-relaxed mb-5">
              <span className="font-semibold text-ink">Genemo</span>は、EC事業者・実店舗・メーカーなど商品を持つすべての事業者向けに、AI が販促コンテンツを自動生成するサービスです。商品画像を入れるだけで、SNS 動画・LP・ブログ記事まで一気に作れます。
            </p>

            <div className="space-y-3.5">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-cyan-50/60 border border-cyan-100">
                <div className="w-9 h-9 rounded-lg bg-cyan-500 flex items-center justify-center shrink-0 shadow-xs">
                  <Film className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-ink">Genemo Short — AI 動画生成</div>
                  <div className="text-xs text-ink-muted leading-relaxed mt-0.5">
                    商品画像を投げ込むだけで TikTok / Reels / YouTube Shorts に投稿できる縦型動画が完成
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-violet-50/60 border border-violet-100">
                <div className="w-9 h-9 rounded-lg bg-violet-500 flex items-center justify-center shrink-0 shadow-xs">
                  <ImageIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-ink">Genemo Image / Page　リリース予定</div>
                  <div className="text-xs text-ink-muted leading-relaxed mt-0.5">
                    同じクレジットで利用できる新サービスを順次リリース予定
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50/60 border border-emerald-100">
                <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0 shadow-xs">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-ink">使った分だけのクレジット制</div>
                  <div className="text-xs text-ink-muted leading-relaxed mt-0.5">
                    AI 生成のたびにクレジット消費。残量はいつでも確認できます
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 p-3.5 rounded-xl bg-amber-50 border-2 border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-amber-800 mb-1">AI コンテンツについて</div>
                  <p className="text-xs text-amber-900 leading-relaxed">
                    {AI_NOTICE}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 右: 利用規約 */}
          <div className="bg-white rounded-2xl p-6 sm:p-7 shadow-xl shadow-sky-100/60 border border-primary flex flex-col">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl font-bold text-ink">利用規約</h2>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary font-semibold inline-flex items-center gap-1 underline-offset-2 hover:underline"
                >
                  別タブで全文 <ExternalLink className="w-3 h-3" />
                </a>
                <span className="text-ink">/</span>
                <a
                  href="/legal/specified-commercial"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink-muted hover:text-ink font-semibold inline-flex items-center gap-1 underline-offset-2 hover:underline"
                >
                  特商法表示 <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            <p className="text-xs text-ink-muted mb-3">
              最終更新日: <span className="font-semibold text-ink">{TOS_LAST_UPDATED}</span>
              <span className="mx-2">／</span>
              バージョン: <span className="font-mono font-semibold text-ink">{TOS_VERSION}</span>
            </p>

            <div className="flex-1 overflow-y-auto rounded-xl bg-surface-2 border border-line p-4 text-xs leading-relaxed max-h-[420px] min-h-[280px]">
              {TOS_ARTICLES.map(a => (
                <section key={a.title} className="mb-4">
                  <h3 className="text-sm font-bold text-ink mb-1.5 pb-1 border-b border-line">
                    {a.title}
                  </h3>
                  <div className="text-ink whitespace-pre-wrap">
                    {a.content}
                  </div>
                </section>
              ))}
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-5 p-4 rounded-xl bg-primary-subtle border-2 border-primary hover:bg-primary-subtle hover:border-primary transition-colors">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                disabled={submitting}
                className="sr-only"
              />
              <div className={`w-5 h-5 mt-0.5 shrink-0 rounded-sm border-2 flex items-center justify-center transition-all ${agreed ? 'bg-primary border-primary' : 'bg-white border-line'}`}>
                {agreed && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="2,6 5,9 10,3" />
                  </svg>
                )}
              </div>
              <span className="text-sm text-ink font-bold leading-snug">
                上記の利用規約および AI 生成物に関する免責事項を読み、内容に同意します
              </span>
            </label>

            {error && (
              <p className="text-sm text-red-700 mt-3 p-3 rounded-lg bg-red-50 border border-red-300 font-semibold">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* 下部 CTA */}
        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!agreed || submitting}
            className="group inline-flex items-center justify-center gap-3 px-12 sm:px-16 py-4 rounded-2xl text-base sm:text-lg font-bold text-white transition-all duration-300 disabled:cursor-not-allowed enabled:hover:scale-[1.03]"
            style={
              agreed && !submitting
                ? {
                    background: 'linear-gradient(135deg, #06b6d4, #2563eb)',
                    boxShadow: '0 10px 30px rgba(6,182,212,0.35)',
                  }
                : {
                    background: '#cbd5e1',
                    color: '#ffffff',
                    boxShadow: 'none',
                  }
            }
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>準備中…</span>
              </>
            ) : (
              <>
                <span>{agreed ? '準備OK！始める' : '上記にチェックして始める'}</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:group-enabled:translate-x-1" />
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleLogout}
            disabled={submitting}
            className="text-xs text-ink-muted hover:text-ink underline-offset-2 hover:underline"
          >
            同意しない（ログアウト）
          </button>
        </div>

      </div>
    </div>
  )
}
